import {
  type ConfigResourceCreateSchemaType,
  configResourceCreateSchema,
} from '@/forms/configResource';
import { useCreateCashServiceMutation } from '@/store/api/services/operations/cashServices';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  TextField,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

const CreateCashService = ({ hide }: ModalChildrenProps) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ConfigResourceCreateSchemaType>({
    resolver: zodResolver(configResourceCreateSchema),
    mode: 'onSubmit',
    defaultValues: { name: '', description: '' },
  });

  const [createCashService] = useCreateCashServiceMutation();

  const onSubmit = async (payload: ConfigResourceCreateSchemaType) => {
    try {
      await createCashService(payload).unwrap();
      toast.success('Cash service created successfully');
      hide('create-cash-service');
    } catch (error: any) {
      const errorMessage =
        error?.meta?.message ||
        error?.data?.message ||
        error?.message ||
        'Failed to create cash service';
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <DialogContent sx={{ py: 3 }}>
        <Stack gap={2}>
          <TextField
            label="Name *"
            fullWidth
            error={!!errors.name}
            helperText={errors?.name?.message}
            size="small"
            inputProps={{ 'data-test': 'config-name-input' }}
            {...register('name')}
          />

          <Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Description *
            </Typography>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <>
                  <TextareaAutosize
                    {...field}
                    data-test="config-description-input"
                    minRows={4}
                    maxRows={8}
                    placeholder="Provide a detailed description of the cash service"
                    style={{
                      width: '100%',
                      resize: 'vertical',
                      padding: '12px 14px',
                      border: errors.description
                        ? '1px solid #d32f2f'
                        : '1px solid rgba(0, 0, 0, 0.23)',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      lineHeight: '1.5',
                      backgroundColor: 'transparent',
                      outline: 'none',
                      cursor: 'text',
                    }}
                  />
                  {errors.description && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.description.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={() => hide('create-cash-service')} data-test="config-cancel-btn">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
          data-test="config-submit-btn"
        >
          {isSubmitting ? 'Creating...' : 'Create Cash Service'}
        </Button>
      </DialogActions>
    </>
  );
};

export default CreateCashService;
